import { getYear, isValid } from 'date-fns';
import Decimal from 'decimal.js';
import {
  ControllerFieldState,
  FieldValues,
  RegisterOptions,
} from 'react-hook-form';

import { diagnostics } from '@/utils/diagnostics';
import { formatDateToMonDDYYYY } from '@/utils/formatting/dates';
import { commaNumber } from '@/utils/formatting/numbers';

let lastId = 0;
export function generateUniqueId(prefix = 'id_') {
  lastId++;
  return `${prefix}${lastId}`;
}

// TODO: There's a lot of logic in RHF about error messages (just check out the interface of fieldState.error)
// and they actually have their own error message component: https://react-hook-form.com/api/useformstate/errormessage
// But I also want to keep the underlying input component unaware of the form lib. We could potentially pass
// in a React.ReactNode as a component prop?
export function getFieldErrorValue(
  fieldState: ControllerFieldState,
  isFormSubmitted: boolean,
  validateOnChange?: boolean
) {
  const isPossibleRenderErrorState = isFormSubmitted || validateOnChange;
  if (isPossibleRenderErrorState && fieldState.error) {
    return fieldState.error.message;
  }
  return undefined;
}

// per IRS instructions, 7520 rates are always rounded to the nearest 2/10s of a percent.
// https://www.irs.gov/businesses/small-businesses-self-employed/section-7520-interest-rates
export function interestRate7520Valuation(value: Decimal | null | undefined) {
  if (!value || value.isNaN()) return undefined;
  const stringValue = value.toFixed(1);
  const isValidDecimal =
    parseInt(stringValue[stringValue.length - 1]!) % 2 === 0;
  if (!isValidDecimal) return '7520 rate must end in an even decimal.';
  return undefined;
}

const globalMinimumDate = new Date('01/01/1900');
export function minimumDateValidation(value: Date | null | undefined) {
  if (!value || !(value instanceof Date)) return undefined;
  if (value <= globalMinimumDate) {
    return `Date must be after ${formatDateToMonDDYYYY(globalMinimumDate)}`;
  }

  return undefined;
}

export function validDateValidation(value: Date | null | undefined) {
  if (!value || !(value instanceof Date)) return undefined;
  if (!isValid(value)) return 'Date must be valid.';
  return undefined;
}

/**
 * Coerces a value read out of form state into an integer we can send as a GraphQL `Int`.
 *
 * Two things conspire to make this necessary. Our text and formatted-number inputs both hand
 * react-hook-form a *string*: `FormAwareTextInput` because it's a plain text field, and
 * `FormAwareFormattedNumberInput` because react-number-format runs in `isNumericString` mode. Form
 * shapes routinely type these fields as `number | null` anyway, so TypeScript can't catch a string
 * reaching a numeric variable. And the server validates an `Int` variable by parsing it as an
 * integer, so it rejects `''` and fractional values alike with `cannot use string as Int` — failing
 * the entire operation, not just that one field.
 *
 * Returns `null` for empty and unparseable values (an emptied input holds `''`) so callers never
 * send `NaN` and can decide what an absent value should mean.
 *
 * This is the backstop, not the user-facing guard: a field the user can type a fraction into also
 * wants `validateNumberRange({ integer: true })` so they get told about it instead of having their
 * input quietly changed. Anything that slips past validation is rounded here, on the grounds that a
 * rounded value beats failing the whole request.
 */
export function parseIntegerFormValue(
  value: number | string | null | undefined
): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.round(parsed) : null;
}

export function getGreaterThanCurrentYearValidation(
  value: number | string | null | undefined
) {
  const numYear = Number(value);
  if (isNaN(numYear)) {
    return `Year must be a number`;
  }

  const currYear = getYear(new Date());
  if (isNaN(numYear) || numYear < currYear) {
    return `Year must be ≥ ${currYear}`;
  }
  return undefined;
}

export function getRequiredValidation<FieldValue = unknown>(
  label: string,
  required: boolean
) {
  return (value: FieldValue) => {
    if (!required) return undefined;
    if (!value || (typeof value === 'string' && !value.trim())) {
      return `${label} is required.`;
    }
    return undefined;
  };
}

interface GetPercentMaxValidationParams {
  disabled?: boolean;
  isDecimalJSInput?: boolean;
  label: string;
  maxValue: number;
}

export function getPercentMaxValidation({
  disabled,
  isDecimalJSInput,
  label,
  maxValue,
}: GetPercentMaxValidationParams) {
  return (value: unknown) => {
    if (disabled) return undefined;
    const errorMessage = `${label} can't exceed ${commaNumber(maxValue)}%`;
    if (isDecimalJSInput && value) {
      const isOverLimit = (value as Decimal).greaterThan(new Decimal(maxValue));
      return isOverLimit ? errorMessage : undefined;
    }
    if (typeof value === 'number' && !isNaN(value)) {
      return value > maxValue ? errorMessage : undefined;
    }

    return undefined;
  };
}

export function getValidations<
  FormShape extends FieldValues,
  FieldValue = unknown,
>(
  label: string,
  required: boolean,
  validation?: RegisterOptions<FormShape>['validate']
): RegisterOptions<FormShape>['validate'] {
  // this pattern allows the caller to pass in a more specific "required" validation
  // function and have it overwrite the default one.
  const allValidations = Object.assign(
    { required: getRequiredValidation<FieldValue>(label, required) },
    validation || {}
  );
  return allValidations;
}

type FieldValueType = Decimal | string | null;

function getFormattedDecimalValue(
  value: FieldValueType,
  decimalScale: number,
  fieldName?: string,
  fixedDecimalScale?: boolean
): string {
  if (Decimal.isDecimal(value)) {
    // When fixedDecimalScale is explicitly false, cap precision without
    // padding trailing zeros (e.g. Decimal(1) → "1", not "1.00000").
    if (fixedDecimalScale === false) {
      return value.toDecimalPlaces(decimalScale).toString();
    }
    return value.toFixed(decimalScale);
  } else if (value === null || value === undefined) {
    return '';
  } else {
    throw new Error(
      `Passing a non-Decimal.js value to a Decimal.js expecting parser${
        fieldName ? ` to field name ${fieldName}` : ''
      }.`
    );
  }
}

interface GetPossiblyDecimalFieldValueOpts {
  isDecimalJS: boolean;
  decimalScale: number;
  fieldName?: string;
  fixedDecimalScale?: boolean;
}

export function getPossiblyDecimalFieldValue(
  value: FieldValueType,
  opts: GetPossiblyDecimalFieldValueOpts = {
    isDecimalJS: false,
    decimalScale: 2,
  }
): string {
  // if it's a Decimal.js input, we expect either null (to render an empty input)
  // or a valid Decimal.js value
  if (opts.isDecimalJS) {
    return getFormattedDecimalValue(
      value,
      opts.decimalScale,
      opts.fieldName,
      opts.fixedDecimalScale
    );
  } else if (Decimal.isDecimal(value)) {
    // we're passing a Decimal value to a non-Decimal input. warn the user.
    diagnostics.warn(
      'Passing a Decimal.js value to an input that\'s not marked with "isDecimalJSInput".'
    );
    return getFormattedDecimalValue(
      value,
      opts.decimalScale,
      opts.fieldName,
      opts.fixedDecimalScale
    );
  }

  return value || '';
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- linter refactor
function getEventWithDecimalValue(event: any) {
  try {
    // instantiating a Decimal with an empty string will throw an error
    const decimalValue = new Decimal(event.target.value);
    if (decimalValue.isNaN()) {
      event.target.value = null;
      return event;
    }
    event.target.value = decimalValue;
    return event;
  } catch (err) {
    event.target.value = null;
    return event;
  }
}

export function getDecimalJSSupportingOnChange(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- linter refactor
  formOnChange: (event: any) => void,
  opts = { isDecimalJS: false }
) {
  if (!opts.isDecimalJS) {
    return formOnChange;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- linter refactor
  return function handleDecimalOnChange(event: any) {
    const normalizedEvent = getEventWithDecimalValue(event);
    formOnChange(normalizedEvent);
  };
}
